import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {SetupWorkspaceController} from '../../../../../../admin/setup-workspace/setup-workspace-controller';
import {SetupWorkspaceRequestImpl} from '../../../../../../admin/setup-workspace/setup-workspace-request';
import {useStoreContext} from '../../../../../../../store/store-context';
import {activateNotification} from '../../../../../../../ui/molecules/notification/activate-notification';
import {generateRandomDigits} from '../../../../../../../utils/generate-random-numbers';
import {setFirebaseData} from '../../../../../../../utils/firebase-handler';
import {
  canMembersEditGoals,
  DEFAULT_GOAL_DISPLAY_NAMES,
  GoalDisplayNameField,
  isDisplayNameDirty,
  memberGoalEditValue,
  normalizeDisplayName,
} from './configure-goals.utils';

type FeatureKey = 'kpi' | 'alignment' | 'autoEnd' | 'memberEdit';

const displayNameLabel = (field: GoalDisplayNameField) =>
  field === 'goalName' ? 'Objectives' : 'Key Results';

export const useConfigureGoals = () => {
  const {authStore} = useStoreContext();
  const auth = authStore.auth;
  const workspace = auth?.user?.workspace;
  const controller = useMemo(
    () => new SetupWorkspaceController(new SetupWorkspaceRequestImpl()),
    [],
  );

  const savedNames = useMemo(
    () => ({
      goalName:
        workspace?.config?.goalName || DEFAULT_GOAL_DISPLAY_NAMES.goalName,
      keyResultName:
        workspace?.config?.keyResultName ||
        DEFAULT_GOAL_DISPLAY_NAMES.keyResultName,
    }),
    [workspace?.config?.goalName, workspace?.config?.keyResultName],
  );

  const [draftNames, setDraftNames] = useState(savedNames);
  const previousSavedNames = useRef(savedNames);
  const [savingName, setSavingName] = useState<GoalDisplayNameField | null>(
    null,
  );
  const [busyFeature, setBusyFeature] = useState<FeatureKey | null>(null);
  const [kpiEnabled, setKpiEnabled] = useState(
    Boolean(workspace?.config?.allowKpi),
  );
  const [alignmentEnabled, setAlignmentEnabled] = useState(
    Boolean(workspace?.settings?.goalAlignment),
  );
  const [autoEndEnabled, setAutoEndEnabled] = useState(
    workspace?.settings?.autoEndGoals === 'enabled',
  );
  const [memberEditEnabled, setMemberEditEnabled] = useState(
    canMembersEditGoals(workspace?.settings?.allowGoalUpdate),
  );

  useEffect(() => {
    const previous = previousSavedNames.current;
    setDraftNames((current) => ({
      goalName: isDisplayNameDirty(current.goalName, previous.goalName)
        ? current.goalName
        : savedNames.goalName,
      keyResultName: isDisplayNameDirty(
        current.keyResultName,
        previous.keyResultName,
      )
        ? current.keyResultName
        : savedNames.keyResultName,
    }));
    previousSavedNames.current = savedNames;
  }, [savedNames]);
  useEffect(() => setKpiEnabled(Boolean(workspace?.config?.allowKpi)), [
    workspace?.config?.allowKpi,
  ]);
  useEffect(
    () => setAlignmentEnabled(Boolean(workspace?.settings?.goalAlignment)),
    [workspace?.settings?.goalAlignment],
  );
  useEffect(
    () => setAutoEndEnabled(workspace?.settings?.autoEndGoals === 'enabled'),
    [workspace?.settings?.autoEndGoals],
  );
  useEffect(
    () =>
      setMemberEditEnabled(
        canMembersEditGoals(workspace?.settings?.allowGoalUpdate),
      ),
    [workspace?.settings?.allowGoalUpdate],
  );

  const persistWorkspacePatch = useCallback(
    (patch: {config?: Record<string, any>; settings?: Record<string, any>}) => {
      const currentAuth = authStore.auth;
      if (!currentAuth?.user?.workspace) return;

      const nextAuth = {
        ...currentAuth,
        user: {
          ...currentAuth.user,
          workspace: {
            ...currentAuth.user.workspace,
            ...(patch.config
              ? {
                  config: {
                    ...currentAuth.user.workspace.config,
                    ...patch.config,
                  },
                }
              : {}),
            ...(patch.settings
              ? {
                  settings: {
                    ...currentAuth.user.workspace.settings,
                    ...patch.settings,
                  },
                }
              : {}),
          },
        },
      };

      authStore.setAuth(nextAuth);
      authStore.storeAuthInLocalStorage(nextAuth);
    },
    [authStore],
  );

  const updateConfig = useCallback(
    async (changes: Record<string, any>) => {
      const currentAuth = authStore.auth;
      const currentWorkspace = currentAuth?.user?.workspace;
      if (!currentWorkspace?.id || !currentAuth?.tokens?.access?.token) {
        return false;
      }

      const config = {...currentWorkspace.config, ...changes};
      const response = await controller.workspaceEdit(
        currentWorkspace.id,
        {config},
        currentAuth.tokens.access.token,
        true,
      );

      if (!response) return false;
      persistWorkspacePatch({config: changes});
      setFirebaseData('updates/settings', generateRandomDigits(16));
      return true;
    },
    [authStore, controller, persistWorkspacePatch],
  );

  const updateSettings = useCallback(
    async (changes: Record<string, any>) => {
      const currentAuth = authStore.auth;
      const currentWorkspace = currentAuth?.user?.workspace;
      if (!currentWorkspace?.id || !currentAuth?.tokens?.access?.token) {
        return false;
      }

      const settings = {...currentWorkspace.settings, ...changes};
      const response = await controller.workspaceEdit(
        currentWorkspace.id,
        {settings},
        currentAuth.tokens.access.token,
        true,
      );

      if (!response) return false;
      persistWorkspacePatch({settings: changes});
      setFirebaseData('updates/settings', generateRandomDigits(16));
      return true;
    },
    [authStore, controller, persistWorkspacePatch],
  );

  const updateDraftName = useCallback(
    (field: GoalDisplayNameField, value: string) =>
      setDraftNames((current) => ({...current, [field]: value})),
    [],
  );

  const cancelDisplayName = useCallback(
    (field: GoalDisplayNameField) =>
      setDraftNames((current) => ({...current, [field]: savedNames[field]})),
    [savedNames],
  );

  const stageDefaultDisplayName = useCallback(
    (field: GoalDisplayNameField) =>
      updateDraftName(field, DEFAULT_GOAL_DISPLAY_NAMES[field]),
    [updateDraftName],
  );

  const saveDisplayName = useCallback(
    async (field: GoalDisplayNameField) => {
      const value = normalizeDisplayName(draftNames[field]);
      if (!value || !isDisplayNameDirty(value, savedNames[field])) return;

      setSavingName(field);
      const success = await updateConfig({
        [field]: value,
        behaviour: 'custom',
      });
      setSavingName(null);

      if (success) {
        setDraftNames((current) => ({...current, [field]: value}));
        activateNotification({
          title: `${displayNameLabel(field)} display name updated`,
          content: `The new label is now used across your workspace.`,
          kind: 'success',
        });
      }
    },
    [draftNames, savedNames, updateConfig],
  );

  const updateFeature = useCallback(
    async (
      feature: FeatureKey,
      nextValue: boolean,
      update: () => Promise<boolean>,
      setLocalValue: (value: boolean) => void,
      label: string,
    ) => {
      setBusyFeature(feature);
      setLocalValue(nextValue);
      try {
        const success = await update();
        if (!success) {
          setLocalValue(!nextValue);
          return false;
        }

        activateNotification({
          title: `${label} ${nextValue ? 'enabled' : 'disabled'}`,
          content: 'Your workspace goal settings have been updated.',
          kind: 'success',
        });
        return true;
      } catch {
        setLocalValue(!nextValue);
        return false;
      } finally {
        setBusyFeature(null);
      }
    },
    [],
  );

  const setKpi = useCallback(
    (enabled: boolean) =>
      updateFeature(
        'kpi',
        enabled,
        () => updateConfig({allowKpi: enabled}),
        setKpiEnabled,
        'KPIs',
      ),
    [updateConfig, updateFeature],
  );

  const setAlignment = useCallback(
    (enabled: boolean) =>
      updateFeature(
        'alignment',
        enabled,
        () =>
          updateSettings({
            goalAlignment: enabled,
            keyResultCascading: enabled,
          }),
        setAlignmentEnabled,
        'Goal alignment and key result cascading',
      ),
    [updateFeature, updateSettings],
  );

  const setAutoEnd = useCallback(
    (enabled: boolean) =>
      updateFeature(
        'autoEnd',
        enabled,
        () => updateSettings({autoEndGoals: enabled ? 'enabled' : 'disabled'}),
        setAutoEndEnabled,
        'Goal auto-end',
      ),
    [updateFeature, updateSettings],
  );

  const setMemberEdit = useCallback(
    (enabled: boolean) =>
      updateFeature(
        'memberEdit',
        enabled,
        () => updateSettings({allowGoalUpdate: memberGoalEditValue(enabled)}),
        setMemberEditEnabled,
        'Member goal editing',
      ),
    [updateFeature, updateSettings],
  );

  return {
    alignmentEnabled,
    autoEndEnabled,
    busyFeature,
    cancelDisplayName,
    draftNames,
    kpiEnabled,
    memberEditEnabled,
    savedNames,
    saveDisplayName,
    savingName,
    setAlignment,
    setAutoEnd,
    setKpi,
    setMemberEdit,
    stageDefaultDisplayName,
    updateDraftName,
  };
};
